import React from "react";
import { Loader2, Phone, Mail, MessageCircle, ShieldCheck } from "lucide-react";
import { phoneDigits } from "@/lib/format";
import { AD } from "@/constants/testIds";

/** SVG logos scrape poorly (missing xmlns, mixed content, etc.) — fall back to a text banner. */
const isSvg = (u) => typeof u === "string" && /\.svg(\?|#|$)/i.test(u);

/**
 * Premium branded agent card — portrait-hero pattern:
 *   - Agent photo is the focal point (large rounded portrait, centered)
 *   - Agency logo sits as a small badge overlapping the bottom-right of the photo
 *   - Agent name → agency line → licence stacked below
 *   - Stacked pill actions (Contact / Email)
 * Deliberately distinct from banner-heavy real-estate agent cards.
 */
export default function AgentCard({ listing, attrs, revealed, revealMut, onReveal, onWhatsAppClick, signedOut = false }) {
  const agencyName = attrs["Agency Name"] || "";
  const agencyLogo = attrs["Agency Logo"] || "";
  const agencyLicense = attrs["Agency License"] || "";

  const agentName = attrs["Secondary Contact"] || revealed?.secondary_contact || listing.seller_name || "Listing agent";
  const agentPhoto = attrs["Secondary Contact Photo"] || revealed?.secondary_contact_photo || listing.seller_photo || "";

  const revealedPhone = revealed?.secondary_contact_phone || revealed?.phone || "";
  const revealedWhatsapp = revealed?.secondary_contact_whatsapp || revealed?.whatsapp || "";
  const revealedEmail = revealed?.secondary_contact_email || revealed?.seller_email || "";

  // Use the parent-provided guarded callback if given (redirects logged-out users to /login).
  // Fall back to raw mutation only when a caller explicitly opts out by omitting onReveal.
  const triggerReveal = onReveal || (() => revealMut?.mutate?.());

  const showLogoImage = agencyLogo && !isSvg(agencyLogo);
  const initial = (agentName || "A").trim().charAt(0).toUpperCase();

  return (
    <div
      data-testid="agent-card"
      className="relative rounded-3xl bg-white shadow-card overflow-hidden border border-soft"
    >
      {/* Agency banner — logo fills the top band, avatar overlaps it in front */}
      {showLogoImage ? (
        <div
          className="relative bg-gradient-to-br from-[#F5F1EA] via-white to-[#EEF3FB] px-6 pt-6 pb-16 flex items-center justify-center"
          data-testid="agent-card-logo-band"
        >
          <img
            src={agencyLogo}
            alt={agencyName || "Agency logo"}
            className="max-h-16 w-auto max-w-[72%] object-contain"
            data-testid="agent-card-logo"
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
          />
        </div>
      ) : (
        <div
          className="relative bg-slate-900 text-white px-6 pt-6 pb-16 flex items-center justify-center"
          data-testid="agent-card-agency-band"
        >
          <div
            className="font-display font-black uppercase tracking-wide text-center leading-tight"
            style={{ fontSize: "clamp(1.15rem, 3.4vw, 1.6rem)" }}
            data-testid="agent-card-agency-name"
          >
            {agencyName || "Independent listing"}
          </div>
        </div>
      )}

      {/* Agent avatar — larger, centered, sitting clearly IN FRONT of the logo banner */}
      <div className="relative z-10 flex justify-center -mt-12">
        {agentPhoto ? (
          <img
            src={agentPhoto}
            alt={agentName}
            data-testid="agent-card-photo"
            className="w-24 h-24 rounded-full object-cover ring-4 ring-white shadow-xl"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div
            data-testid="agent-card-avatar-fallback"
            className="w-24 h-24 rounded-full ring-4 ring-white shadow-xl grid place-items-center bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] text-3xl font-black"
          >
            {initial}
          </div>
        )}
      </div>

      {/* Text block — name, agency line (when logo shown), licence */}
      <div className="relative px-6 pt-3 text-center">
        <div className="font-display font-bold text-lg text-slate-900" data-testid="agent-card-name">
          {agentName}
        </div>

        {showLogoImage && agencyName && (
          <div className="mt-0.5 text-sm text-slate-600" data-testid="agent-card-agency-sub">
            at <span className="font-semibold text-slate-800">{agencyName}</span>
          </div>
        )}

        {agencyLicense && (
          <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500" data-testid="agent-card-license">
            <ShieldCheck className="w-3 h-3 text-emerald-500" />
            {agencyLicense}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="mt-4 mx-6 border-t border-dashed border-slate-200" />

      {/* Actions */}
      <div className="relative px-6 py-5 space-y-2.5">
        <button
          type="button"
          data-testid={AD.revealPhoneBtn}
          onClick={triggerReveal}
          disabled={revealMut?.isPending || !!revealedPhone}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-semibold bg-slate-100 hover:bg-slate-200 text-slate-800 disabled:opacity-70 transition-colors"
        >
          {revealMut?.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Phone className="w-4 h-4" />
          )}
          {revealedPhone ? (
            <a
              href={`tel:${phoneDigits(revealedPhone)}`}
              className="tabular-nums"
              data-testid={AD.phoneNumber}
              onClick={(e) => e.stopPropagation()}
            >
              {revealedPhone}
            </a>
          ) : (
            /* Iter-375 — explicit sign-in copy for anonymous visitors */
            signedOut ? "Sign in to view contact details" : "Contact details"
          )}
        </button>

        {revealedEmail ? (
          <a
            data-testid="agent-card-email"
            href={`mailto:${revealedEmail}?subject=${encodeURIComponent(`Enquiry: ${listing.title}`)}`}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-semibold bg-slate-900 hover:bg-slate-800 text-white transition-colors"
          >
            <Mail className="w-4 h-4" /> Email enquiry
          </a>
        ) : (
          <button
            type="button"
            data-testid="agent-card-email-reveal"
            onClick={triggerReveal}
            disabled={revealMut?.isPending || !!revealed}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-semibold bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-70 transition-colors"
          >
            <Mail className="w-4 h-4" /> Email enquiry
          </button>
        )}

        {revealedWhatsapp && (
          <a
            data-testid={AD.whatsappLink}
            href={`https://wa.me/${phoneDigits(revealedWhatsapp)}?text=${encodeURIComponent(`Kia Ora — I'm interested in "${listing.title}"`)}`}
            target="_blank"
            rel="noreferrer"
            onClick={onWhatsAppClick}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-semibold text-white transition-colors"
            style={{ backgroundColor: "var(--whatsapp)" }}
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp agent
          </a>
        )}
      </div>
    </div>
  );
}
